import { computed, action } from "mobx";
import http from "@app/lib/http";
import BaseStore from "./base";
import project from "@app/state/store/projects/setup";
import report from "@app/state/store/report";
import ChatHistory from "@app/state/model/ai/chat-history";
import notify from "@app/components/notify/index";
import { AI_SUGGESTION_KEY } from "@app/constants";
import { ws } from "@app/lib/socket";
import aiChatStore from "@app/state/store/ai-chat";

export class AI extends BaseStore {
    currentRequest = null;
    pendingSource = null;
    pendingQuestion = null;
    activeSourceId = null;

    observable() {
        return {
            loading: false,
            questions: [],
            chatHistory: [],
            cancelled: false,
            draftQuestion: "",
        };
    }

    constructor() {
        super();

        ws.on("ai.prompt.status", (data) => {
            if (data.status === "cancelled") {
                return;
            }

            if (this.activeSourceId === data.source) {
                this.clearPendingQuestion();
                if (this.pendingSource) {
                    this.getChatHistory(this.pendingSource);
                }
            }

            if (
                data.status === "success" &&
                aiChatStore.activeDocument?.id === data.source &&
                aiChatStore.projectId === data.project
            ) {
                aiChatStore.updateActiveDocumentFileStatus(true);
            }
        });
    }

    @computed get busy() {
        return this.loading;
    }

    @computed get project() {
        return project.id || report.id;
    }

    @action clearPendingQuestion() {
        this.pendingQuestion = null;
    }

    @action
    async load() {
        try {
            this.loading = true;

            const { data: questionsData } = await http.get(`/ai/questions`);

            this.questions = questionsData;
        } catch (error) {
            notify.error(error.response?.data?.error);
        } finally {
            this.loading = false;
        }
    }

    @action
    async question(source, question, saveHistory = true, withHistory = true) {
        if (!source || !question) {
            return;
        }

        try {
            this.loading = true;
            this.cancelled = false;
            this.pendingSource = source;
            this.pendingQuestion = question;
            this.draftQuestion = "";

            console.log("Asking question:", question, "for source:", source);

            if (!this.project) {
                return;
            }

            this.currentRequest = http.put(`/project/${this.project}/ai/question`, {
                source,
                question,
                saveHistory,
                withHistory,
            });

            await this.currentRequest;

            if (this.currentRequest?.canceled) {
                return;
            }
        } catch (error) {
            if (!this.cancelled) {
                notify.error(
                    "An error occurred while preparing your response. Please try again or contact support for assistance.",
                );
            }
            this.pendingSource = null;
        } finally {
            this.loading = false;
            this.currentRequest = null;
        }
    }

    /**
     * Cancel the current prompt request
     */
    @action
    async cancelPrompt() {
        this.cancelled = true;

        if (this.currentRequest) {
            this.currentRequest.cancel();
            this.currentRequest = null;
        }

        const processingMessage = this.chatHistory.find((item) => item.processing || item.indexing);

        if (processingMessage && this.project) {
            try {
                await http.post(
                    `/project/${this.project}/ai/chat-history/${processingMessage._id}/cancel`,
                );
            } catch (ex) {
                notify.error("Error cancelling prompt.");
                return;
            }
        }

        if (processingMessage) {
            this.chatHistory = this.chatHistory.filter(
                (item) => item._id !== processingMessage._id,
            );
        }

        if (this.pendingQuestion) {
            this.draftQuestion = this.pendingQuestion;
        }

        this.loading = false;
        this.pendingSource = null;
        this.pendingQuestion = null;

        notify.info("Prompt cancelled.");
    }

    @action
    async getChatHistory(source) {
        if (!source) {
            return;
        }

        try {
            this.loading = true;

            if (!this.project) {
                return;
            }

            this.activeSourceId = source._id;

            let { data } = await http.get(`/project/${this.project}/ai/chat-history`, {
                source,
            });

            if (data) {
                this.chatHistory = data.map((item) => new ChatHistory(item));
            }
            return this.chatHistory;
        } catch (error) {
            notify.error(error.response?.data?.error);
        } finally {
            this.loading = false;
        }
    }

    @action isValidSuggestion(suggestion) {
        return (
            suggestion &&
            suggestion !== AI_SUGGESTION_KEY.NOT_REQUIRED &&
            suggestion !== `<p>${AI_SUGGESTION_KEY.NOT_REQUIRED}</p>\n` &&
            suggestion !== AI_SUGGESTION_KEY.NO_MATCH
        );
    }
}

export default new AI();
